import {
  DropdownSortOptions,
  Column,
  Grid,
  Filter,
  ValidFilterValues,
  Pagination,
  MobilePagination
} from '@120wateraudit/envirio-components'
import { DocumentNode } from 'graphql'
import { isEmpty, omit, debounce } from 'lodash'
import * as React from 'react'
import { Query } from '@apollo/client/react/components'
import { ApolloClient } from '@apollo/client'
import ReactPlaceholder from 'react-placeholder'
import { Responsive } from '../components/Responsive/Responsive'
interface FilterMap {
  [filterName: string]: Filter
}

interface GetCustomFieldsFilterStateArg {
  action: 'applied' | 'removed'
  filter: Filter
  value?: any
  customFieldFilters: any
  operator?: any
  value2?: any
}

interface GetCustomFieldVariablesArg {
  filters: { [key: string]: ValidFilterValues }
  customFieldFilters: any
}

interface GQLGridProps {
  client?: ApolloClient<any>
  query: DocumentNode
  variables: { [key: string]: any } // generic type
  columns: Column[]
  defaultFilters?: { [key in string]: Filter['value'] }
  availableFilters: FilterMap
  totalGetter?(data: any): number
  defaultSorts?: { [key in string]: SortDirection }
  sorts?: { [key in string]: SortDirection }
  renderRow(item: any, index?: number): React.ReactNode
  renderTable?(data: any): JSX.Element
  entityName?: string
  pluralEntityName?: string
  additionalMenuItems?: React.ReactNode | undefined
  noResultsFound?: JSX.Element
  parentRef?: any
  sortMainFilterOptions?: DropdownSortOptions
  /**
   * todo: The consumer has to know to implement both funcs if these if it wants custom field filters?
   */

  getCustomFieldsFilterState?(a: GetCustomFieldsFilterStateArg): any // TODO: type this
  getCustomFieldVariables?(a: GetCustomFieldVariablesArg): any
  breakFiltersVertically?: boolean
  searchBarStyle?: React.CSSProperties
  hideTimeInDateTimeFilter?: boolean
  style?: any
  searchPlaceholder?: string
  unstackable?: boolean
}

interface GQLGridState {
  filters: { [key in string]: Filter['value'] }
  filterOperators: { [key in string]: string }
  filterValues2: { [key in string]: any }
  customFieldFilters: { [key in string]: any }
  sorts: { [key in string]: SortDirection }
  searchValue: string | undefined
  valueToBeSearched: string | undefined
  page: number
  pageSize: number
  artificialFetch: boolean
}

export class GQLGrid extends React.Component<GQLGridProps, GQLGridState> {
  query: any
  variables: any
  refetch: any
  constructor(props: GQLGridProps) {
    super(props)
    const filters = {
      ...(this.props.defaultFilters || {}) // TODO: refactor, include ops/extra values?
    }

    if (props.parentRef) {
      props.parentRef(this)
    }

    this.state = {
      page: 0,
      pageSize: 10,
      filters,
      customFieldFilters: [],
      sorts: this.props.defaultSorts || {},
      searchValue: undefined,
      valueToBeSearched: undefined,
      filterOperators: {},
      filterValues2: {},
      artificialFetch: false
    }
  }

  handleColumnClicked = (refetch: () => void) => (column: Column) => {
    if (!column.sortable) {
      return
    }

    const { sorts = {} } = this.state

    if (sorts[column.key]) {
      if (sorts[column.key] === 'ASC') {
        sorts[column.key] = 'DESC'
      } else {
        delete sorts[column.key]
      }
    } else {
      sorts[column.key] = 'ASC'
    }

    this.setState({ sorts }, refetch)
  }

  handleFilterApplied =
    (refetch: () => void) =>
    (
      filter: Filter,
      value: any, // todo: type this
      operator: any, // todo: type this
      value2?: any // todo: code smell. needs better data type to support n values
    ) => {
      const { filters, customFieldFilters } = this.state
      const { getCustomFieldsFilterState } = this.props

      let updatedCustomFieldFilters = customFieldFilters
      if (typeof getCustomFieldsFilterState === 'function') {
        updatedCustomFieldFilters = getCustomFieldsFilterState({
          action: 'applied',
          filter,
          value,
          customFieldFilters,
          operator,
          value2: value2 === undefined ? null : value2
        })
      }

      this.setState(
        {
          filters: {
            ...filters,
            [filter.key]: value
          },
          customFieldFilters: updatedCustomFieldFilters,
          filterOperators: {
            ...this.state.filterOperators,
            [filter.key]: operator
          },
          filterValues2: {
            ...this.state.filterValues2,
            [filter.key]: value2
          },
          page: 0
        },
        refetch
      )
    }

  handleFilterRemoved = (refetch: () => void) => (filter: Filter) => {
    const { filters, customFieldFilters, filterOperators, filterValues2 } =
      this.state
    const { getCustomFieldsFilterState } = this.props

    let updatedCustomFieldFilters = customFieldFilters

    if (typeof getCustomFieldsFilterState === 'function') {
      updatedCustomFieldFilters = getCustomFieldsFilterState({
        action: 'removed',
        filter,
        customFieldFilters
      })
    }

    if (filters) {
      this.setState(
        {
          filters: omit(filters, [filter.key]),
          filterOperators: omit(filterOperators, [filter.key]),
          filterValues2: omit(filterValues2, [filter.key]),
          customFieldFilters: updatedCustomFieldFilters,
          page: 0
        },
        refetch
      )
    }
  }

  private handleSearchChanged = (refetch: () => void) => (e: any) => {
    ;(this.setState({
      searchValue: e.target.value,
      artificialFetch: true,
      page: 0
    }),
      this.searchValue(e.target.value, refetch))
  }

  private searchValue = debounce((value: string, refetch: any) => {
    this.setState(
      {
        valueToBeSearched: value,
        artificialFetch: false
      },
      refetch
    )
  }, 300)

  getAvailableFilters = () => {
    const filterOptions = Object.values(this.props.availableFilters)
    // code smell, should not have name clash here. why doesn't this function
    // return this.props.availableFilters?
    return filterOptions.filter(f => this.state.filters[f.key] === undefined)
  }

  getSelectedFilters = () => {
    const { availableFilters } = this.props
    const { filters, filterOperators, filterValues2 } = this.state
    const filterOptions = Object.values(availableFilters)

    return filterOptions
      .filter(f => filters.hasOwnProperty(f.key))
      .map(fo => ({
        ...fo,
        label: fo.label,
        value: filters[fo.key],
        operator: filterOperators[fo.key],
        value2: filterValues2[fo.key]
      }))
  }

  handlePageChanged =
    (refetch: () => void) =>
    (_: any, { activePage: page }: any) => {
      this.setState({ page: page - 1 }, refetch)
    }

  handlePageSizeChanged =
    (refetch: () => void) =>
    ({ value: pageSize }: { value: any }) => {
      this.setState({ pageSize, page: 0 }, refetch)
    }

  getExportVariables = () => {
    const {
      filters,
      sorts = { createdOn: 'DESC' },
      searchValue,
      customFieldFilters
    } = this.state
    const { getCustomFieldVariables } = this.props

    let filterVariables = filters

    if (typeof getCustomFieldVariables === 'function') {
      filterVariables = getCustomFieldVariables({ filters, customFieldFilters })
    }

    return {
      filters: filterVariables,
      sorts,
      search: searchValue
    }
  }

  fetchData = () => {
    this.forceUpdate()
  }

  public getVariables = () => {
    const { variables } = this.props

    const {
      sorts,
      filters,
      page,
      pageSize,
      valueToBeSearched,
      customFieldFilters
    } = this.state
    const { getCustomFieldVariables } = this.props

    let filterVariables = filters
    if (typeof getCustomFieldVariables === 'function') {
      filterVariables = getCustomFieldVariables({ filters, customFieldFilters })
    }

    return {
      ...variables,
      page,
      pageSize,
      sorts: isEmpty(sorts) ? { createdOn: 'DESC' } : sorts,
      search: valueToBeSearched,
      filters: filterVariables
    }
  }

  public getRefetch = () => {
    if (this.refetch && typeof this.refetch === 'function') {
      this.refetch()
    }
  }

  render() {
    const {
      query,
      renderRow,
      columns,
      totalGetter,
      entityName,
      pluralEntityName,
      additionalMenuItems,
      noResultsFound,
      sortMainFilterOptions,
      client,
      renderTable,
      breakFiltersVertically = false,
      searchBarStyle = {},
      hideTimeInDateTimeFilter,
      style,
      searchPlaceholder,
      unstackable
    } = this.props

    const { sorts, page, pageSize, searchValue, artificialFetch } = this.state

    return (
      <Query client={client} query={query} variables={this.getVariables()}>
        {({ data, loading, refetch }: any) => {
          // todo: type this query generically
          let items = []
          let totalPages = 0
          let total = 0

          if (!this.refetch && refetch) {
            this.refetch = refetch
          }

          if (!loading && !isEmpty(data)) {
            const values = Object.values(data)

            items = (values[0] as any).items
            totalPages = (values[0] as any).totalPages
            if (totalGetter) {
              total = totalGetter(data)
            }
          }

          return (
            <>
              <Grid
                renderTable={renderTable}
                isFetching={loading || artificialFetch}
                breakFiltersVertically={breakFiltersVertically}
                style={style || { margin: '1rem 0' }}
                data={items}
                count={total}
                columns={columns}
                filters={this.getAvailableFilters()}
                selectedFilters={this.getSelectedFilters()}
                sorted={sorts}
                searchValue={searchValue}
                renderRow={renderRow}
                noResultsFound={noResultsFound}
                onColumnClicked={this.handleColumnClicked(refetch)}
                onFilterApplied={this.handleFilterApplied(refetch)}
                onFilterRemoveClicked={this.handleFilterRemoved(refetch)}
                onSearchChanged={this.handleSearchChanged(refetch)}
                entityName={entityName || 'item'}
                pluralEntityName={pluralEntityName || 'items'}
                additionalMenuItems={additionalMenuItems}
                sortMainFilterOptions={sortMainFilterOptions}
                searchBarStyle={searchBarStyle}
                hideTimeInDateTimeFilter={hideTimeInDateTimeFilter}
                searchPlaceholder={searchPlaceholder}
                unstackable={unstackable}
                gridLoader={
                  <ReactPlaceholder
                    type="text"
                    showLoadingAnimation
                    rows={1}
                    ready={false}
                  >
                    <></>
                  </ReactPlaceholder>
                }
                loaderRows={5}
                hideShowingText={true}
              />
              {this.renderPagination(
                {
                  page,
                  pageSize,
                  totalPages,
                  refetch
                },
                this.getShowText(items, total, undefined)
              )}
            </>
          )
        }}
      </Query>
    )
  }

  private getShowText(items: any, count: any, totalOnPage: any): string {
    const { entityName = 'item', pluralEntityName = 'items' } = this.props
    const total = count ? count : items.length
    if (total < 1) {
      return ''
    }
    if (total === 1) {
      const singular = entityName
      return `Showing 1 ${singular}`
    }

    const plural = pluralEntityName
    const numberOnPage = totalOnPage ? totalOnPage : items.length

    return `Showing ${numberOnPage} of ${total} ${plural}`
  }

  private renderPagination(
    { page, pageSize, totalPages, refetch }: RenderPaginationArg,
    showingText: string
  ): JSX.Element {
    return (
      <>
        <Responsive platform="desktop">
          <Pagination
            page={page + 1}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChanged={this.handlePageChanged(refetch)}
            onPageSizeChanged={this.handlePageSizeChanged(refetch)}
            siblingRange={0}
            showingText={() => (
              <div className="pagination__showingText">{showingText}</div>
            )}
          />
        </Responsive>

        <Responsive platform="mobile">
          <MobilePagination
            page={page + 1}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChanged={this.handlePageChanged(refetch)}
            onPageSizeChanged={this.handlePageSizeChanged(refetch)}
            boundaryRange={1}
            siblingRange={0}
            showingText={() => (
              <div className="pagination__showingText">{showingText}</div>
            )}
            styles={{
              displayDropdown: {
                paddingBottom: '8px'
              }
            }}
          />
        </Responsive>
      </>
    )
  }
}

interface RenderPaginationArg {
  page: number
  pageSize: number
  totalPages: number
  refetch(): void
}

type SortDirection = 'DESC' | 'ASC'
